/**
 * bootTrace — o "Safari Web Inspector de pobre".
 *
 * ── Por que isto existe ─────────────────────────────────────────────────────
 * O app trava no boot dentro de um TestFlight, num aparelho que não temos, sem
 * Mac para abrir o Web Inspector. `console.log` nesse cenário não existe para
 * ninguém: ele vai para um console que ninguém consegue abrir.
 *
 * Então o log passa a viver **na memória do app** e a ter uma porta de saída
 * dentro da própria UI (toque longo na tela de carregamento). O que a pessoa
 * reporta por print vira o diagnóstico.
 *
 * ── Decisões ────────────────────────────────────────────────────────────────
 * 1. **Buffer em memória, não `localStorage`.** O boot pode travar ANTES de o
 *    storage estar disponível (é justamente um dos suspeitos em WKWebView), e
 *    um `try/catch` de storage no caminho crítico é mais uma coisa que pode
 *    pendurar. O log morre com o app — e tudo bem: o defeito que ele
 *    diagnostica acontece na abertura, então sempre há um log fresco.
 * 2. **Teto de entradas.** Um retry em laço não pode virar vazamento de
 *    memória num app que já está com problema.
 * 3. **Sem dado pessoal.** O painel é feito para virar print e ser enviado por
 *    WhatsApp: nada de e-mail, uid ou token. `detail` é para mensagem de erro
 *    e nome de etapa.
 */

import { BootTimeoutError } from "./withTimeout";

export type BootPhase = "start" | "ok" | "fail" | "timeout" | "skip" | "info";

export interface BootEntry {
  /** Nome curto e estável da etapa (ex.: `auth-state`). */
  step: string;
  phase: BootPhase;
  /** ms desde o carregamento do módulo — é o eixo do tempo do boot. */
  at: number;
  /** Duração da etapa, presente nas fases terminais. */
  ms?: number;
  /** Mensagem de erro ou observação curta. */
  detail?: string;
}

const MAX_ENTRIES = 120;

const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
const entries: BootEntry[] = [];
const listeners = new Set<() => void>();

/**
 * Snapshot CACHEADO do log.
 *
 * `useSyncExternalStore` (o painel) exige que `getSnapshot` devolva o MESMO
 * valor por identidade enquanto nada muda — devolver `entries.slice()` a cada
 * chamada faz o React ver um estado novo a cada render e entrar em laço
 * infinito ("Maximum update depth exceeded"). O array só é recriado quando o
 * log de fato muda, que é o contrato que a API espera.
 */
let snapshot: readonly BootEntry[] = [];

function now(): number {
  const n = typeof performance !== "undefined" ? performance.now() : Date.now();
  return Math.round(n - t0);
}

function emit(): void {
  snapshot = entries.slice();
  for (const l of listeners) l();
}

/** Registra uma entrada avulsa. Usado para marcos que não são etapas. */
export function bootMark(step: string, phase: BootPhase, detail?: string): void {
  if (entries.length >= MAX_ENTRIES) entries.shift();
  entries.push({ step, phase, at: now(), detail });
  emit();
}

/** Snapshot imutável do log — a fonte do painel e do texto copiável. */
export function getBootLog(): readonly BootEntry[] {
  return snapshot;
}

/** Assinatura para o painel re-renderizar enquanto o boot corre. */
export function subscribeBootLog(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Só para os testes: o buffer é global por desenho. */
export function __resetBootLogForTests(): void {
  entries.length = 0;
  snapshot = [];
  listeners.clear();
}

function describe(err: unknown): string {
  if (err instanceof BootTimeoutError) return err.code;
  if (err instanceof Error) {
    // `code` do Firebase (ex.: `auth/network-request-failed`) diz mais que a
    // mensagem, que costuma ser prosa longa.
    const code = (err as { code?: unknown }).code;
    if (typeof code === "string" && code) return `${code}: ${err.message}`;
    return err.message;
  }
  return String(err);
}

/**
 * Envolve uma etapa do boot: marca início e desfecho, e mede a duração.
 *
 * **Não** engole o erro — quem chama decide o que fazer. O trace é observação,
 * nunca política: uma etapa que falha e é tratada como sucesso porque "o log já
 * registrou" é exatamente o tipo de silêncio que produziu este bug.
 */
export async function traceStep<T>(step: string, run: () => Promise<T>): Promise<T> {
  const started = now();
  bootMark(step, "start");
  try {
    const value = await run();
    entries.push({ step, phase: "ok", at: now(), ms: now() - started });
    emit();
    return value;
  } catch (err) {
    const phase: BootPhase = err instanceof BootTimeoutError ? "timeout" : "fail";
    entries.push({
      step,
      phase,
      at: now(),
      ms: now() - started,
      detail: describe(err),
    });
    emit();
    throw err;
  }
}

/** Texto de uma linha por etapa — o que o painel mostra e o que se copia. */
export function formatBootLog(log: readonly BootEntry[] = getBootLog()): string {
  return log
    .map((e) => {
      const dur = e.ms === undefined ? "" : ` ${e.ms}ms`;
      const detail = e.detail ? ` — ${e.detail}` : "";
      return `${String(e.at).padStart(6)}ms ${e.phase.padEnd(7)} ${e.step}${dur}${detail}`;
    })
    .join("\n");
}
